import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../api";
import ChatAttachment from "./ChatAttachment";
import MessageReadStatus from "./MessageReadStatus";
import useChatMessages from "../hooks/useChatMessages";
import { attachmentError, chatError } from "../utils/chat";
import bgVideo from "../assets/bg.mp4";
function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
}
export default function AdminMessages() {
  const [conversations, setConversations] = useState([]),
    [selectedTenantId, setSelectedTenantId] = useState(null),
    [selectedThreadId, setSelectedThreadId] = useState(null);
  const [draft, setDraft] = useState(''),
    [attachment, setAttachment] = useState(null),
    [sending, setSending] = useState(false),
    [opening, setOpening] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(true),
    [directoryError, setDirectoryError] = useState(''),
    [actionError, setActionError] = useState(''),
    [search, setSearch] = useState('');
  const selection = useRef({
      tenantId: null,
      threadId: null,
      epoch: 0
    }),
    directoryRequest = useRef(0),
    drafts = useRef({}),
    fileRef = useRef(null),
    sendingRef = useRef(false),
    alive = useRef(true);
  const chat = useChatMessages(selectedThreadId);
  const {
    messages,
    scrollRef
  } = chat;
  const loadingMessages = opening || chat.loading;
  const error = actionError || directoryError || chat.error;
  const fetchConversations = useCallback(async () => {
    const sequence = ++directoryRequest.current;
    try {
      const res = await api.get('/admin/chat/tenants');
      if (!alive.current || sequence !== directoryRequest.current) return;
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      items.sort((a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0));
      setConversations(items);
      setDirectoryError('');
      if (!selection.current.tenantId) {
        const first = items.find(item => item.thread_id);
        if (first) {
          selection.current = {
            tenantId: first.tenant_id,
            threadId: first.thread_id,
            epoch: selection.current.epoch + 1
          };
          setSelectedTenantId(first.tenant_id);
          setSelectedThreadId(first.thread_id);
        }
      }
    } catch {
      if (alive.current && sequence === directoryRequest.current) setDirectoryError('Unable to load conversations. Please retry.');
    } finally {
      if (alive.current) setLoadingConversations(false);
    }
  }, []);
  useEffect(() => {
    alive.current = true;
    fetchConversations();
    let stopped = false,
      timer;
    const poll = async () => {
      if (!document.hidden) await fetchConversations();
      if (!stopped) timer = setTimeout(poll, 3000);
    };
    timer = setTimeout(poll, 3000);
    const visible = () => {
      if (!document.hidden) fetchConversations();
    };
    document.addEventListener('visibilitychange', visible);
    return () => {
      alive.current = false;
      stopped = true;
      // This is a request counter, not a DOM ref; invalidate the latest poll.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      directoryRequest.current++;
      selection.current.epoch++;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [fetchConversations]);
  const filteredConversations = useMemo(() => conversations.filter(item => [item.tenant_name, item.tenant_email, item.last_message_preview].filter(Boolean).join(' ').toLowerCase().includes(search.trim().toLowerCase())), [conversations, search]);
  const selectedConversation = conversations.find(item => item.tenant_id === selectedTenantId);
  const openConversation = async item => {
    if (sendingRef.current) return;
    if (selection.current.tenantId) drafts.current[selection.current.tenantId] = {
      draft,
      attachment
    };
    const saved = drafts.current[item.tenant_id] || {
      draft: '',
      attachment: null
    };
    setDraft(saved.draft);
    setAttachment(saved.attachment);
    if (fileRef.current) fileRef.current.value = '';
    const epoch = selection.current.epoch + 1;
    selection.current = {
      tenantId: item.tenant_id,
      threadId: item.thread_id || null,
      epoch
    };
    setSelectedTenantId(item.tenant_id);
    setSelectedThreadId(item.thread_id || null);
    setActionError('');
    setOpening(!item.thread_id);
    if (item.thread_id) return;
    try {
      const res = await api.post('/chat/threads', {
        tenant_id: item.tenant_id
      });
      if (!alive.current || selection.current.epoch !== epoch) return;
      const id = res.data?.thread?.id;
      if (!id) throw new Error('Missing thread');
      selection.current.threadId = id;
      setSelectedThreadId(id);
      fetchConversations();
    } catch {
      if (alive.current && selection.current.epoch === epoch) setActionError('Unable to start conversation. Select the tenant again to retry.');
    } finally {
      if (alive.current && selection.current.epoch === epoch) setOpening(false);
    }
  };
  const handleSend = async event => {
    event.preventDefault();
    const threadId = selection.current.threadId;
    if (!threadId || opening || sendingRef.current || (!draft.trim() && !attachment)) return;
    const invalid = attachmentError(attachment);
    if (invalid) {
      setActionError(invalid);
      return;
    }
    sendingRef.current = true;
    setSending(true);
    setActionError('');
    try {
      const data = new FormData();
      if (draft.trim()) data.append('body', draft.trim());
      if (attachment) data.append('attachment', attachment);
      await api.post('/chat/threads/' + threadId + '/messages', data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (!alive.current) return;
      setDraft('');
      setAttachment(null);
      drafts.current[selection.current.tenantId] = {
        draft: '',
        attachment: null
      };
      if (fileRef.current) fileRef.current.value = '';
      await chat.refresh();
      await fetchConversations();
    } catch (error) {
      if (alive.current) setActionError(chatError(error, 'Failed to send message. Your draft is saved; please retry.'));
    } finally {
      sendingRef.current = false;
      if (alive.current) setSending(false);
    }
  };
  const chooseAttachment = event => {
    const file = event.target.files?.[0] || null;
    const invalid = attachmentError(file);
    if (invalid) {
      setActionError(invalid);
      event.target.value = '';
      return;
    }
    setAttachment(file);
    setActionError('');
  };
  return <div className="relative min-h-screen overflow-hidden bg-slate-950 font-sans text-white">
      <video data-workspace-background="true" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" src={bgVideo} autoPlay muted loop playsInline />
      <div className="absolute inset-0 bg-slate-950/86" />

      <div className="workspace-page-body relative z-10 min-h-screen px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-5 border-b border-white/10 pb-5">
          <p className="text-xs font-bold uppercase tracking-[0.35em] text-teal-300">
            Communication
          </p>
          <h1 className="mt-2 text-3xl font-bold tracking-wide md:text-4xl">
            Tenant Messages
          </h1>
          <p className="mt-2 text-sm text-slate-300 md:text-base">
            Send messages and share documents with your tenants.
          </p>
        </div>

        <div className="grid gap-5 xl:grid-cols-[320px_minmax(0,1fr)]">
          <section className="rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl">
            <input value={search} onChange={e => setSearch(e.target.value)} className="mb-4 w-full rounded-lg border border-white/10 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30" placeholder="Search tenant or message" />

            {loadingConversations ? <p className="py-6 text-center text-sm text-slate-400">Loading...</p> : filteredConversations.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No tenants found.</p> : <div className="max-h-[62vh] space-y-2 overflow-y-auto pr-1">
                {filteredConversations.map(item => {
              const active = item.tenant_id === selectedTenantId;
              return <button disabled={sending} key={item.tenant_id} onClick={() => openConversation(item)} className={`w-full rounded-lg border px-3 py-3 text-left transition ${active ? "border-teal-300/40 bg-teal-500/15" : "border-white/10 bg-white/5 hover:bg-white/10"}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-white">{item.tenant_name || "Tenant"}</p>
                          <p className="text-xs text-slate-400">{item.tenant_email || "-"}</p>
                        </div>
                        {!!item.unread_count && <span className="rounded-full bg-teal-500 px-2 py-0.5 text-[11px] font-bold text-white">
                            {item.unread_count} unread
                          </span>}
                      </div>
                      <p className="mt-2 line-clamp-2 text-xs text-slate-300">
                        {item.last_message_preview || "No messages yet"}
                      </p>
                      <p className="mt-1 text-[11px] text-slate-500">
                        {item.last_message_at ? formatDateTime(item.last_message_at) : "No activity"}
                      </p>
                    </button>;
            })}
              </div>}
          </section>

          <section className="flex h-[70vh] min-h-[420px] flex-col rounded-lg border border-white/10 bg-slate-950/90 shadow-xl">
            <div className="border-b border-white/10 px-4 py-3">
              <p className="text-lg font-semibold text-white">
                {selectedConversation?.tenant_name || "Select a conversation"}
              </p>
              <p className="text-xs text-slate-400">{selectedConversation?.tenant_email || ""}</p>
            </div>

            <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {chat.hasOlder && <button type="button" className="ws-button ws-button-secondary" disabled={chat.loadingOlder} onClick={chat.loadOlder}>{chat.loadingOlder ? "Loading…" : "Load older messages"}</button>}
              {!selectedTenantId ? <p className="text-sm text-slate-400">Choose a tenant from the list.</p> : loadingMessages ? <p className="text-sm text-slate-400">Loading messages...</p> : messages.length === 0 ? <p className="text-sm text-slate-400">No messages yet. Start the conversation.</p> : messages.map(message => {
              const mine = message.sender_role === "admin";
              return <div key={message.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] rounded-lg border px-3 py-2 text-sm ${mine ? "border-teal-300/40 bg-teal-500/15 text-teal-100" : "border-white/10 bg-white/10 text-slate-100"}`}>
                        {message.body && <p className="whitespace-pre-wrap break-words">{message.body}</p>}
                        {message.attachment_url && <ChatAttachment message={message} />}
                        <p className="mt-1 text-[11px] text-slate-300/80">{formatDateTime(message.created_at)}{mine && <MessageReadStatus message={message} />}</p>
                      </div>
                    </div>;
            })}
            </div>

            <form onSubmit={handleSend} className="border-t border-white/10 px-4 py-3">
              {error && <div role="alert" className="mb-2 rounded-lg border border-red-300/30 bg-red-500/15 px-3 py-2 text-sm text-red-100">
                  {error} <button type="button" className="underline" onClick={() => {
                fetchConversations();
                chat.refresh();
              }}>Reload</button>
                </div>}
              <div className="mb-2 grid gap-2 md:grid-cols-[minmax(0,1fr)_auto]">
                <textarea aria-label="Message" maxLength={4000} disabled={sending} value={draft} onChange={e => setDraft(e.target.value)} rows={2} placeholder="Type your message..." className="w-full resize-none rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30" />
                <label className="cursor-pointer rounded-lg border border-white/15 px-3 py-2 text-xs font-semibold text-slate-200 transition hover:bg-white/10">
                  Attach
                  <input ref={fileRef} disabled={sending} type="file" accept=".jpg,.jpeg,.png,.pdf" className="sr-only" onChange={chooseAttachment} />
                </label>
              </div>
              {attachment && <p className="mb-2 text-xs text-slate-300">Attachment: {attachment.name} <button type="button" disabled={sending} className="underline" onClick={() => {
                setAttachment(null);
                if (fileRef.current) fileRef.current.value = "";
              }}>Remove attachment</button></p>}
              <button type="submit" disabled={!selectedThreadId || opening || sending || (!draft.trim() && !attachment)} className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-500 disabled:opacity-60">
                {sending ? "Sending..." : "Send"}
              </button>
            </form>
          </section>
        </div>
      </div>
    </div>;
}
